import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BlockerFunction, useBeforeUnload, useBlocker } from 'react-router-dom';

import { ConfirmModal } from '@/components/ConfirmModal';
import { Button } from '@/components/ui/button';

import { sameValue } from './draft-diff';
import { PolicyDrafts, PolicySectionKey, PolicySections } from './policy-update';

interface DraftEntry<T> {
    draft: T;
    count: number;
    /** The saved data the draft was started from. */
    baseline: T;
    /** The saved data as it is now; differs from `baseline` when it changed while the draft was open. */
    latest: T;
}

type DraftEntries = { [K in PolicySectionKey]?: DraftEntry<PolicySections[K]> };

interface PolicyChangesValue {
    entries: DraftEntries;
    count: number;
    /** True when the saved data behind a pending draft changed since editing began, so saving would overwrite it. */
    stale: boolean;
    saving: boolean;
    error?: string;
    setEntry: <K extends PolicySectionKey>(key: K, entry: DraftEntry<PolicySections[K]> | undefined) => void;
    trackLatest: <K extends PolicySectionKey>(key: K, latest: PolicySections[K]) => void;
    discardAll: () => void;
    save: () => void;
}

const PolicyChangesContext = createContext<PolicyChangesValue | undefined>(undefined);

/**
 * Collects change requests from the Investment Policy sections. Sections never persist anything themselves: they
 * edit a draft through {@link usePolicyDraft}, and the page-level bar saves or discards all drafts together.
 */
export const PolicyChangesProvider = ({
    onSave,
    children,
}: {
    /** Persists every draft in one go. Rejecting keeps the drafts so the user can retry. */
    onSave: (drafts: PolicyDrafts) => Promise<unknown>;
    children: ReactNode;
}) => {
    const [entries, setEntries] = useState<DraftEntries>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>();
    const setEntry = useCallback(
        <K extends PolicySectionKey>(key: K, entry: DraftEntry<PolicySections[K]> | undefined) => {
            setEntries((current) => {
                const { [key]: _removed, ...rest } = current;
                return entry ? { ...rest, [key]: entry } : rest;
            });
        },
        [],
    );
    const trackLatest = useCallback(<K extends PolicySectionKey>(key: K, latest: PolicySections[K]) => {
        setEntries((current) => {
            const entry = current[key];
            return entry && !sameValue(entry.latest, latest) ? { ...current, [key]: { ...entry, latest } } : current;
        });
    }, []);
    const discardAll = useCallback(() => {
        setEntries({});
        setError(undefined);
    }, []);
    const save = useCallback(async () => {
        setSaving(true);
        setError(undefined);
        try {
            await onSave(entries);
            setEntries({});
        } catch {
            setError('Couldn’t save your changes. They’re still here.');
        } finally {
            setSaving(false);
        }
    }, [entries, onSave]);
    const pending = useMemo((): DraftEntry<unknown>[] => Object.values(entries), [entries]);
    const count = pending.reduce((total, entry) => total + entry.count, 0);
    const dirty = count > 0;
    // Leaving the page (another route or tab) would drop the drafts. Hash changes, which the section nav uses to
    // scroll within the page, stay on it.
    const shouldBlock = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname,
        [dirty],
    );
    const blocker = useBlocker(shouldBlock);
    // Closing or reloading the browser tab can't be intercepted by the router; the browser shows its own prompt.
    useBeforeUnload(
        useCallback(
            (event: BeforeUnloadEvent) => {
                if (!dirty) return;
                event.preventDefault();
                event.returnValue = '';
            },
            [dirty],
        ),
    );
    const value = useMemo(
        () => ({
            entries,
            count,
            stale: pending.some((entry) => !sameValue(entry.baseline, entry.latest)),
            saving,
            error,
            setEntry,
            trackLatest,
            discardAll,
            save,
        }),
        [entries, pending, count, saving, error, setEntry, trackLatest, discardAll, save],
    );
    return (
        <PolicyChangesContext.Provider value={value}>
            {children}
            <ConfirmModal
                open={blocker.state === 'blocked'}
                onOpenChange={(open) => {
                    if (!open) blocker.reset?.();
                }}
                title="You have unsaved changes"
                description="Are you sure you want to leave? Your unsaved investment policy changes will be lost."
                confirmText="Leave"
                cancelText="Stay"
                confirmVariant="destructive"
                onConfirm={() => blocker.proceed?.()}
                onCancel={() => blocker.reset?.()}
            />
        </PolicyChangesContext.Provider>
    );
};

const usePolicyChanges = () => {
    const context = useContext(PolicyChangesContext);
    if (!context) throw new Error('usePolicyChanges must be used within a PolicyChangesProvider');
    return context;
};

/**
 * A section's editable draft. Reads as `baseline` until the first change, and drops back to it (clearing the
 * pending count) when `countChanges` reports nothing differs. Updaters receive the current draft.
 */
export const usePolicyDraft = <K extends PolicySectionKey>(
    key: K,
    baseline: PolicySections[K],
    countChanges: (draft: PolicySections[K], baseline: PolicySections[K]) => number,
) => {
    type T = PolicySections[K];
    const { entries, setEntry, trackLatest } = usePolicyChanges();
    const entry: DraftEntry<T> | undefined = entries[key];
    const draft = entry ? entry.draft : baseline;
    const hasDraft = !!entry;
    // Lets the provider notice when the saved data moves under an open draft.
    useEffect(() => {
        if (hasDraft) trackLatest(key, baseline);
    }, [hasDraft, key, baseline, trackLatest]);
    const setDraft = (update: (current: T) => T) => {
        const next = update(draft);
        const count = countChanges(next, baseline);
        setEntry(
            key,
            count > 0
                ? { draft: next, count, baseline: entry ? entry.baseline : baseline, latest: baseline }
                : undefined,
        );
    };
    return [draft, setDraft] as const;
};

/** True while the drafts are being saved; sections lock editing so nothing changes under the save. */
export const usePolicySaving = () => usePolicyChanges().saving;

export const PolicyChangesBar = () => {
    const { count, stale, saving, error, discardAll, save } = usePolicyChanges();
    if (count === 0) return null;
    return (
        <div
            role="region"
            aria-label="Pending policy changes"
            className="fixed bottom-6 left-1/2 z-10 flex w-[820px] max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-3.5 rounded-xl bg-[#2C2423] py-3 pr-3.5 pl-4 text-white shadow-[0_12px_32px_rgba(40,30,28,0.28)]"
        >
            <span
                aria-hidden="true"
                className="flex size-7 items-center justify-center rounded-full bg-white text-[13px] font-bold text-[#2C2423]"
            >
                {count}
            </span>
            <div className="flex-1" role="status">
                <div className="text-[15px] font-medium">
                    {count} {count === 1 ? 'change' : 'changes'} pending
                </div>
                <div className={error ? 'text-xs text-[#F2B8B5]' : 'text-xs text-[#CFC6C1]'}>
                    {error ??
                        (stale
                            ? 'Saved data changed since you started editing. Saving will overwrite it.'
                            : 'Not saved yet')}
                </div>
            </div>
            <Button
                variant="ghost"
                className="text-white hover:bg-transparent hover:text-[#CFC6C1]"
                disabled={saving}
                onClick={discardAll}
            >
                Cancel
            </Button>
            <Button
                variant="outline"
                className="border-[#5A4F4D] bg-transparent text-white hover:bg-[#3A302F] hover:text-white"
                disabled={saving}
                onClick={save}
            >
                {saving ? 'Saving…' : stale ? 'Save anyway' : 'Save'}
            </Button>
        </div>
    );
};
